import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  createContext,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type PaperColor = 'lemon' | 'apricot' | 'pink' | 'lilac' | 'sky' | 'mint' | 'sage' | 'sand';
export { clsx };

// ---------------------------------------------------------------- кнопки

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'on-paper' | 'paper-light' | 'link';
  loading?: boolean;
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading, className, disabled, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(variant === 'link' ? 'btn-link' : ['btn', `btn-${variant}`], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {children}
    </button>
  );
});

// ---------------------------------------------------------------- поля

type FieldProps = {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
  className?: string;
};
export function Field({ label, hint, error, children, className }: FieldProps) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: контрол передаётся детьми
    <label className={clsx('field', className)}>
      <span>{label}</span>
      {children}
      {error ? <span className="err">{error}</span> : hint ? <small>{hint}</small> : null}
    </label>
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function Input({ className, invalid, ...rest }, ref) {
  return (
    <input ref={ref} className={clsx('input', className)} aria-invalid={invalid || undefined} {...rest} />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { pill?: boolean }
>(function Select({ className, pill, ...rest }, ref) {
  return <select ref={ref} className={clsx(pill ? 'select-pill' : 'select-input', className)} {...rest} />;
});

// ---------------------------------------------------------------- контейнеры

export function Tray({
  title,
  count,
  icon,
  action,
  children,
  className,
}: {
  title?: ReactNode;
  count?: number;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx('tray tray-pad', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-base font-semibold">
          {icon}
          {title}
          {count !== undefined && <span className="count-pill ml-auto">{count}</span>}
          {action && <span className={count === undefined ? 'ml-auto' : ''}>{action}</span>}
        </h3>
      )}
      {children}
    </section>
  );
}

export function Seg<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode }[];
  label: string;
  className?: string;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: сегментированный переключатель; fieldset ломает раскладку
    <div className={clsx('seg', className)} role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          data-v={o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const Chip = ({ color, small }: { color: PaperColor; small?: boolean }) => (
  <span className={clsx('paper chip', small && 'chip-sm')} data-c={color} aria-hidden="true" />
);

export const Avatar = ({ name, small }: { name: string; small?: boolean }) => (
  <span className={clsx('avatar', small && 'sm')} aria-hidden="true">
    {name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)}
  </span>
);

/** Стопка: несколько занятий одним элементом. */
export function Pile({
  colors,
  title,
  sub,
  onClick,
  className,
  style,
  label,
}: {
  colors: PaperColor[];
  title: ReactNode;
  sub: ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  label?: string;
}) {
  const cs = [...new Set(colors)];
  return (
    <button
      type="button"
      className={clsx('pile', className)}
      onClick={onClick}
      aria-label={label}
      style={{
        ...style,
        ['--c1' as string]: `var(--p-${cs[0]})`,
        ['--c2' as string]: `var(--p-${cs[1] ?? cs[0]})`,
      }}
    >
      <span className="sheet s1" aria-hidden="true" />
      <span className="sheet s2" aria-hidden="true" />
      <span className="face">
        <span className="pile-n">{title}</span>
        <span className="pile-sub">{sub}</span>
        <span className="pile-dots" aria-hidden="true">
          {cs.slice(0, 6).map((c) => (
            <i key={c} style={{ background: `var(--p-${c}-strip)` }} />
          ))}
        </span>
      </span>
    </button>
  );
}

export const DoneMark = ({ draw }: { draw?: boolean }) => (
  <svg className={clsx('done-mark', draw && 'draw')} viewBox="0 0 40 34" aria-hidden="true">
    <path d="M4 18 C 9 22, 12 26, 15 30 C 21 18, 28 9, 37 3" />
  </svg>
);

// ---------------------------------------------------------------- диалог

export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  strip,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  strip?: PaperColor;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="dialog"
      onClose={onClose}
      onCancel={onClose}
      style={
        strip ? ({ ['--strip' as string]: `var(--p-${strip}-strip)` } as React.CSSProperties) : undefined
      }
    >
      <div className="dialog-head">
        <h2 className="text-xl font-semibold">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-ink-3">{subtitle}</p>}
      </div>
      <div className="px-6">{children}</div>
      {footer && <div className="flex items-center gap-3 px-6 pt-4 pb-6">{footer}</div>}
    </dialog>
  );
}

// ---------------------------------------------------------------- тосты

type ToastMsg = { id: number; text: string; kind: 'info' | 'error' };
const ToastCtx = createContext<(text: string, kind?: 'info' | 'error') => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<ToastMsg | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const show = useCallback((text: string, kind: 'info' | 'error' = 'info') => {
    clearTimeout(timer.current);
    setMsg({ id: Date.now(), text, kind });
    timer.current = setTimeout(() => setMsg(null), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div role="status" aria-live="polite">
        {msg && (
          <div key={msg.id} className={clsx('toast', msg.kind === 'error' && 'error')}>
            {msg.text}
          </div>
        )}
      </div>
    </ToastCtx.Provider>
  );
}

// ---------------------------------------------------------------- тема

export function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark' | null>(() => {
    try {
      return (localStorage.getItem('aurea-theme') as 'light' | 'dark' | null) ?? null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    // Без явного выбора тему ставит скрипт в index.html по системной настройке.
    if (theme) document.documentElement.dataset.theme = theme;
  }, [theme]);
  const isDark = theme ? theme === 'dark' : document.documentElement.dataset.theme === 'dark';
  const toggle = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    try {
      localStorage.setItem('aurea-theme', next);
    } catch {}
  };
  return { isDark, toggle };
}
